import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ListMusic, Calendar, ChevronRight, Share2, Trash2, Music } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const PlaylistsPage = () => {
  const navigate = useNavigate();
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDate, setNewDate] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [creating, setCreating] = useState(false);

  const fetchPlaylists = () => {
    fetch(`${BACKEND_URL}/api/playlists`)
      .then(r => r.json())
      .then(setPlaylists)
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchPlaylists(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/playlists`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName, service_date: newDate || null, description: newDesc || null, song_ids: [] })
      });
      const data = await resp.json();
      navigate(`/playlists/${data.id}/edit`);
    } catch (err) { console.error(err); }
    setCreating(false);
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this playlist?')) return;
    await fetch(`${BACKEND_URL}/api/playlists/${id}`, { method: 'DELETE' });
    setPlaylists(prev => prev.filter(p => p.id !== id));
  };

  const handleShare = (id, e) => {
    e.stopPropagation();
    const url = `${window.location.origin}/playlists/${id}`;
    navigator.clipboard.writeText(url);
    alert('Playlist link copied!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center pt-20" data-testid="playlists-loading">
        <div className="w-12 h-12 border-4 border-amber-200 border-t-amber-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pt-16" data-testid="playlists-page">
      {/* Hero */}
      <div className="bg-stone-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <div className="flex items-center gap-3 mb-2">
            <ListMusic className="w-5 h-5 text-amber-400" />
            <span className="text-amber-400 text-sm font-semibold tracking-widest uppercase">Worship Planning</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-3" data-testid="playlists-title">
            Service Playlists
          </h1>
          <p className="text-stone-400 text-base sm:text-lg max-w-xl">
            Create worship setlists for your services. Arrange songs, then share with the congregation.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Create Button */}
        <button
          data-testid="create-playlist-btn"
          onClick={() => setShowCreate(!showCreate)}
          className="w-full sm:w-auto mb-6 flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-md hover:shadow-lg"
        >
          <Plus className="w-5 h-5" /> New Playlist
        </button>

        {/* Create Form */}
        {showCreate && (
          <form onSubmit={handleCreate} className="bg-white rounded-xl border border-stone-200 shadow-sm p-6 mb-6 space-y-4" data-testid="create-playlist-form">
            <div>
              <label className="block text-sm font-semibold text-stone-700 mb-1.5">Playlist Name *</label>
              <input
                data-testid="playlist-name-input"
                type="text"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="e.g. Sunday Morning Worship - March 8"
                className="w-full px-4 py-2.5 bg-stone-50 border border-stone-200 rounded-lg text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-500"
                required
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-1.5">Service Date</label>
                <input
                  data-testid="playlist-date-input"
                  type="date"
                  value={newDate}
                  onChange={e => setNewDate(e.target.value)}
                  className="w-full px-4 py-2.5 bg-stone-50 border border-stone-200 rounded-lg text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-1.5">Description</label>
                <input
                  data-testid="playlist-desc-input"
                  type="text"
                  value={newDesc}
                  onChange={e => setNewDesc(e.target.value)}
                  placeholder="Optional notes"
                  className="w-full px-4 py-2.5 bg-stone-50 border border-stone-200 rounded-lg text-stone-800 focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button data-testid="submit-playlist-btn" type="submit" disabled={creating} className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-2.5 rounded-lg font-semibold transition-all disabled:opacity-50">
                {creating ? 'Creating...' : 'Create & Add Songs'}
              </button>
              <button type="button" onClick={() => setShowCreate(false)} className="text-stone-500 hover:text-stone-700 px-4 py-2.5 font-medium">
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Playlists List */}
        {playlists.length === 0 ? (
          <div className="bg-white rounded-xl border border-stone-200 p-12 text-center" data-testid="playlists-empty">
            <ListMusic className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-stone-800 mb-2">No playlists yet</h3>
            <p className="text-stone-500 mb-4">Create your first worship service playlist to get started.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="playlists-list">
            {playlists.map(pl => (
              <div
                key={pl.id}
                data-testid={`playlist-item-${pl.id}`}
                onClick={() => navigate(`/playlists/${pl.id}`)}
                className="bg-white rounded-xl border border-stone-200 hover:border-amber-200 hover:shadow-md p-5 cursor-pointer transition-all group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-amber-50 group-hover:bg-amber-100 rounded-xl flex items-center justify-center transition-colors">
                    <Music className="w-6 h-6 text-amber-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-stone-800 group-hover:text-amber-700 transition-colors truncate">
                      {pl.name}
                    </h3>
                    <div className="flex items-center gap-3 mt-1">
                      {pl.service_date && (
                        <span className="text-xs text-stone-400 flex items-center gap-1">
                          <Calendar className="w-3 h-3" /> {new Date(pl.service_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      )}
                      <span className="text-xs text-stone-400">
                        {pl.song_ids.length} {pl.song_ids.length === 1 ? 'song' : 'songs'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      data-testid={`playlist-share-${pl.id}`}
                      onClick={e => handleShare(pl.id, e)}
                      className="p-2 text-stone-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-all"
                      title="Copy share link"
                    >
                      <Share2 className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`playlist-edit-${pl.id}`}
                      onClick={e => { e.stopPropagation(); navigate(`/playlists/${pl.id}/edit`); }}
                      className="p-2 text-stone-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all text-xs font-semibold"
                      title="Edit"
                    >
                      Edit
                    </button>
                    <button
                      data-testid={`playlist-delete-${pl.id}`}
                      onClick={e => handleDelete(pl.id, e)}
                      className="p-2 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <ChevronRight className="w-4 h-4 text-stone-300 group-hover:text-amber-500 transition-colors" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlaylistsPage;
